import { useState } from 'react';
import { UMButton, UMFormGroup, UMFormStyles, UMInput, UMSegmentedControl, UMSidePanel } from '../../urbanmap/_components';
import { Pill } from '../proposition-c/ui';
import { fmt } from './domain';
import { DjuForm } from './DjuForm';
import { ImpactPanel } from './ImpactPanel';
import { RatioSheet } from './RatioSheet';
import { UMR, caption } from './tokens';
import type { DjuSettings, PlanBuilding, PlanProject, RatioTable as Ratios, Referential } from './types';

export interface SettingsDraft {
  name: string;
  ratios: Ratios;
  dju: DjuSettings;
}

interface Props {
  project: PlanProject;
  buildings: PlanBuilding[];
  referential: Referential;
  draft: SettingsDraft;
  onDraftChange: (patch: Partial<SettingsDraft>) => void;
  dirty: boolean;
  saving: boolean;
  justSaved: boolean;
  revision: number;
  onSave: () => void;
  onCancel: () => void;
  onClose: () => void;
}

const TABS = ['Général', 'Ratios', 'DJU'] as const;
type Tab = (typeof TABS)[number];

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: `1px solid ${UMR.borderSubtle}` }}>
      <span style={{ color: UMR.textSecondary }}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** Paramètres projet : Général (nom, périmètre en lecture), Ratios, DJU — mêmes contenus que l'assistant. */
export function SettingsPanel(p: Props) {
  const [tab, setTab] = useState<Tab>('Général');
  const [ratiosValid, setRatiosValid] = useState(true);
  const [djuValid, setDjuValid] = useState(true);
  const valid = ratiosValid && djuValid && !!p.draft.name.trim();
  const impact = (
    <ImpactPanel layout="row" buildings={p.buildings} ratios={p.draft.ratios} dju={p.draft.dju} defaultRatios={p.referential.defaultRatios} defaultDju={p.referential.defaultDju} />
  );
  const perimeter = p.project.perimeter;

  return (
    <UMSidePanel
      side="right"
      width={720}
      title="Paramètres du projet"
      onClose={p.onClose}
      footer={
        <>
          <span style={{ marginRight: 'auto', display: 'flex', alignItems: 'center' }}>
            {p.dirty
              ? <Pill tone="warning" icon="edit_note">Modifications non enregistrées</Pill>
              : p.justSaved ? <Pill tone="success" icon="check">Enregistré</Pill> : <span style={caption}>Aucune modification</span>}
          </span>
          <UMButton variant="secondary" disabled={!p.dirty || p.saving} onClick={p.onCancel}>Annuler</UMButton>
          <UMButton icon="save" disabled={!p.dirty || !valid || p.saving} onClick={p.onSave}>{p.saving ? 'Enregistrement…' : 'Enregistrer'}</UMButton>
        </>
      }
    >
      <UMFormStyles />
      <div data-comment-view={`settings-${tab}`} style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 14 }}>
        <UMSegmentedControl options={[...TABS]} value={tab} onChange={(v) => { setTab(v as Tab); setRatiosValid(true); setDjuValid(true); }} />

        {tab === 'Général' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <UMFormGroup label="Nom du projet">
              <UMInput value={p.draft.name} ariaLabel="Nom du projet" onChange={(name) => p.onDraftChange({ name })} />
            </UMFormGroup>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Périmètre <span style={{ ...caption, fontWeight: 400 }}>(lecture seule)</span></div>
              <ReadOnly label="Type" value={perimeter.kind === 'drawn' ? `Zone dessinée (${perimeter.polygon.length} sommets)` : 'Communes'} />
              <ReadOnly label="Périmètre" value={p.project.perimeterLabel} />
              <ReadOnly label="Bâtiments copiés" value={fmt(p.project.buildingCount)} />
              <ReadOnly label="Copie UrbanMap du" value={p.project.snapshotDate} />
              <ReadOnly label="Propriétaire" value={p.project.owner} />
            </div>
          </div>
        )}

        {tab === 'Ratios' && (
          <>
            <RatioSheet key={p.revision} value={p.draft.ratios} defaults={p.referential.defaultRatios} onChange={(ratios) => p.onDraftChange({ ratios })} onValidityChange={setRatiosValid} />
            {impact}
          </>
        )}

        {tab === 'DJU' && (
          <>
            <DjuForm key={p.revision} value={p.draft.dju} defaults={p.referential.defaultDju} stations={p.referential.stations} onChange={(dju) => p.onDraftChange({ dju })} onValidityChange={setDjuValid} />
            {impact}
          </>
        )}

        <div style={caption}>La carte affiche un aperçu en direct ; les consommations du projet changent à l’enregistrement.</div>
      </div>
    </UMSidePanel>
  );
}
